// Contractual current area is not the office's operational footprint.
// Absent commencement/end dates leave the current window open; boundaries are inclusive.
export function dateOnly(value) {
  return value instanceof Date ? value.toISOString().slice(0, 10)
    : value == null ? null : String(value).slice(0, 10);
}

// Do not manufacture zero from blank strings, booleans or absent values.
export function validFootage(value) {
  if (typeof value !== "number" && !(typeof value === "string" && value.trim())) return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

export function storedLeaseGrossSf(lease) {
  const size = validFootage(lease?.size);
  const factor = /^(sqm|sq m|m2)$/i.test(lease?.areaType || "") ? 10.76391041671 : 1;
  return size == null ? null : validFootage(size * factor);
}

// Single current-window predicate for both lifecycle and canonical area math.
function isCurrentLeaseArea(area, today) {
  const start = dateOnly(area.commencementDate);
  const end = dateOnly(area.expirationDate);
  return (!start || start <= today) && (!end || end >= today) && !area.isInBuild;
}

// Non-active overlaps resolve deterministically: in_build > expired > upcoming.
export function classifyLeaseArea(area, asOf = new Date()) {
  const today = dateOnly(asOf);
  if (isCurrentLeaseArea(area, today)) return "active";
  if (area.isInBuild) return "in_build";
  const end = dateOnly(area.expirationDate);
  if (end && end < today) return "expired";
  return "upcoming";
}

// Returns { areas, active }: shallow area copies with status, and the active
// subset of those copies. Neither the lease nor its input area records is mutated.
export function classifyLeaseAreas(lease, asOf = new Date()) {
  const today = dateOnly(asOf);
  const areas = (Array.isArray(lease?.areas) ? lease.areas : [])
    .map((area) => ({ ...area, status: classifyLeaseArea(area, today) }));
  return { areas, active: areas.filter((area) => area.status === "active") };
}

export function currentLeaseAreaBasis(lease, asOf = new Date()) {
  const today = dateOnly(asOf);
  const areas = Array.isArray(lease?.areas) ? lease.areas : [];
  if (!areas.length) {
    return { grossSf: storedLeaseGrossSf(lease),
      usableSf: null, areaIds: [], source: "legacy_lease_size", asOf: today };
  }
  const current = areas.filter((area) => isCurrentLeaseArea(area, today));
  const sum = (field) => current.length && current.every((area) =>
    validFootage(area[field]) != null)
    ? current.reduce((total, area) => total + Number(area[field]), 0) : null;
  return { grossSf: sum("sizeSf"), usableSf: sum("usableSizeSf"),
    areaIds: current.map((area) => area.id), source: "current_lease_areas", asOf: today };
}

export function squareFeetPerSeat(grossSf, seats) {
  return grossSf != null && Number.isFinite(Number(grossSf)) && Number(grossSf) >= 0
    && seats != null && Number.isFinite(Number(seats)) && Number(seats) > 0
    ? Number(grossSf) / Number(seats) : null;
}

// Read projections carry the resolver result so list/detail/export share an as-of
// date. Local edits with complete areas resolve afresh instead of using stale data.
export function canonicalLeaseFootprint(lease, asOf) {
  if (asOf !== undefined || Array.isArray(lease?.areas)) {
    return currentLeaseAreaBasis(lease, asOf);
  }
  return lease?.currentAreaBasis ?? currentLeaseAreaBasis(lease);
}

// Presentation only. Keep canonical measurement evidence intact for annual /
// historical consumers and JSON. A recorded zero must never trigger fallback.
export function currentLeaseFootprint(lease, asOf) {
  const canonical = canonicalLeaseFootprint(lease, asOf);
  const derived = validFootage(canonical.grossSf);
  const stored = storedLeaseGrossSf(lease);
  const fallbackUsed = derived == null && stored != null;
  return { ...canonical, grossSf: derived ?? stored,
    source: fallbackUsed ? "legacy_lease_size" : canonical.source,
    fallbackUsed, canonicalSource: canonical.source, derivedGrossSf: derived };
}

// Only list $/SF uses today's displayed footprint. Annual Financials and
// historical schedule rates must continue using their period-matched basis.
export function displayedLeasePsf(lease) {
  const sqft = currentLeaseFootprint(lease).grossSf;
  const rent = lease?.listDisplayRentUsd !== undefined ? lease.listDisplayRentUsd : lease?.annualRentUsd;
  const number = typeof rent === "number" || (typeof rent === "string" && rent.trim()) ? Number(rent) : NaN;
  return sqft > 0 && Number.isFinite(number) ? number / sqft : null;
}

// Rent detail has its own historical-period scope. Only expose its current
// period rate when its contributing suites exactly match current footage.
export function currentPeriodPsf(lease, period, asOf = new Date()) {
  const basis = currentLeaseAreaBasis(lease, asOf);
  const ids = new Set(period?.contributions?.map((area) => area.areaId));
  return period?.complete && period.annualizedRent != null && basis.grossSf > 0
    && basis.areaIds.length > 0 && ids.size === basis.areaIds.length
    && basis.areaIds.every((id) => ids.has(id))
    ? period.annualizedRent / basis.grossSf : null;
}
